import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { getBillingState, BillingSubscription } from '../../lib/billingState';
import {
  Star,
  Users,
  Briefcase,
  Shield,
  CheckCircle,
  Loader2,
  AlertCircle,
  Calendar,
  RefreshCw,
} from 'lucide-react';

const IS_SANDBOX      = import.meta.env.VITE_PAYPAL_ENV === 'sandbox';
const PAYPAL_CLIENT_ID = IS_SANDBOX
  ? import.meta.env.VITE_PAYPAL_SANDBOX_CLIENT_ID
  : import.meta.env.VITE_PAYPAL_LIVE_CLIENT_ID;
const MONTHLY_PLAN_ID = IS_SANDBOX
  ? import.meta.env.VITE_SANDBOX_MONTHLY_PLAN_ID
  : import.meta.env.VITE_LIVE_MONTHLY_PLAN_ID;
const YEARLY_PLAN_ID = IS_SANDBOX
  ? import.meta.env.VITE_SANDBOX_YEARLY_PLAN_ID
  : import.meta.env.VITE_LIVE_YEARLY_PLAN_ID;

declare global {
  interface Window {
    paypal: {
      Buttons: (opts: Record<string, unknown>) => { render: (sel: string) => void };
    };
  }
}

type Cycle = 'monthly' | 'yearly';

// Explicit state machine — every render state is named and intentional.
// idle              → PayPal buttons visible, user has not interacted
// creating          → PayPal popup open, subscription being created
// awaiting_approval → PayPal approved by user; polling DB for webhook confirmation
// approved          → webhook confirmed activation; navigating to success page
// failed            → PayPal error / cancel / polling timeout — show retry
type CheckoutState = 'idle' | 'creating' | 'awaiting_approval' | 'approved' | 'failed';

// Poll billing_subscriptions until activated_at is set (webhook confirmed)
// or until MAX_POLL_ATTEMPTS is exhausted.
const MAX_POLL_ATTEMPTS = 20;
const POLL_INTERVAL_MS  = 2000;

function LogoHeader() {
  return (
    <div className="w-full flex justify-center items-center py-6 bg-white border-b border-gray-100">
      <img
        src="/perplexity-new-logo.png"
        alt="perplexity-new-logo.png"
        className="h-28 sm:h-32 object-contain"
      />
    </div>
  );
}

export default function Checkout() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [cycle, setCycle]                 = useState<Cycle>('monthly');
  const [sdkLoaded, setSdkLoaded]         = useState(false);
  const [sdkError, setSdkError]           = useState('');
  const [checkoutState, setCheckoutState] = useState<CheckoutState>('idle');
  const [checkoutError, setCheckoutError] = useState('');

  // Single render lock — prevents double-mounting buttons on React strict-mode
  // double-invocation or rapid cycle toggles.
  const buttonsRendered = useRef(false);
  const scriptLoaded    = useRef(false);
  // Abort signal for polling — cleared on unmount so no state updates fire after leaving page.
  const pollStopped     = useRef(false);

  // Poll billing_subscriptions until getBillingState() === 'PRO_ACTIVE'.
  // This is the single authoritative check — all field comparisons live in getBillingState().
  const waitForActivation = useCallback(async (): Promise<boolean> => {
    pollStopped.current = false;
    for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
      if (pollStopped.current) return false;
      const { data } = await supabase
        .from('billing_subscriptions')
        .select('*')
        .eq('user_id', userId!)
        .maybeSingle();
      if (getBillingState(data as BillingSubscription | null) === 'PRO_ACTIVE') {
        return true;
      }
      await new Promise<void>((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
    return false;
  }, [userId]);

  // Stop polling if user navigates away before confirmation arrives.
  useEffect(() => {
    return () => { pollStopped.current = true; };
  }, []);

  // Load PayPal SDK once we have a userId.
  useEffect(() => {
    if (!userId) return;
    if (scriptLoaded.current) return;
    scriptLoaded.current = true;

    if (!PAYPAL_CLIENT_ID) {
      setSdkError('PayPal client ID is not configured.');
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&vault=true&intent=subscription`;
    script.async = true;
    script.onload  = () => setSdkLoaded(true);
    script.onerror = () => {
      console.error('[Checkout] Failed to load PayPal SDK script.');
      setSdkError('Failed to load PayPal SDK. Please check your connection and reload.');
    };
    document.body.appendChild(script);
  }, [userId]);

  // Render PayPal buttons — only when SDK is ready AND state is idle.
  useEffect(() => {
    if (!sdkLoaded || !userId || checkoutState !== 'idle') return;
    if (buttonsRendered.current) return;
    buttonsRendered.current = true;

    const container = document.getElementById('paypal-button-container');
    if (container) container.innerHTML = '';

    const planId = cycle === 'monthly' ? MONTHLY_PLAN_ID : YEARLY_PLAN_ID;

    try {
      window.paypal
        .Buttons({
          style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'subscribe' },

          createSubscription: (_data: unknown, actions: { subscription: { create: (o: Record<string, unknown>) => Promise<string> } }) => {
            setCheckoutState('creating');
            return actions.subscription.create({ plan_id: planId, custom_id: userId });
          },

          onApprove: async (_data: unknown, _actions: unknown) => {
            // PayPal SDK fires onApprove as soon as the user clicks Subscribe
            // in the popup — the webhook has NOT fired yet. We must NOT navigate
            // or assume Pro access at this point.
            setCheckoutState('awaiting_approval');

            const confirmed = await waitForActivation();

            if (pollStopped.current) return; // unmounted — abort silently

            if (confirmed) {
              setCheckoutState('approved');
              navigate(`/workday-client/billing/success?cycle=${cycle}`);
            } else {
              // Polling timed out — payment was received by PayPal but webhook is
              // still in flight. Send the user to success page; its own poller will
              // detect activation once the webhook arrives (can take up to ~60s).
              navigate(`/workday-client/billing/success?cycle=${cycle}&pending=1`);
            }
          },

          onError: (err: unknown) => {
            console.error('[Checkout] PayPal error:', err);
            setCheckoutError('Payment failed. Please try again.');
            setCheckoutState('failed');
            buttonsRendered.current = false;
          },

          onCancel: () => {
            setCheckoutError('Payment cancelled. Try again when ready.');
            setCheckoutState('failed');
            buttonsRendered.current = false;
          },
        } as Record<string, unknown>)
        .render('#paypal-button-container');
    } catch (err) {
      console.error('[Checkout] Failed to render PayPal buttons:', err);
      setSdkError('Failed to render PayPal buttons. Please reload the page.');
    }
  }, [sdkLoaded, checkoutState, cycle, userId, navigate]);

  const handleRetry = () => {
    setCheckoutError('');
    setCheckoutState('idle');
    buttonsRendered.current = false;
  };

  const handleCycleChange = (newCycle: Cycle) => {
    if (newCycle === cycle) return;
    setCycle(newCycle);
    // Reset so buttons re-render with the new plan_id
    buttonsRendered.current = false;
    if (checkoutState === 'failed') {
      setCheckoutError('');
      setCheckoutState('idle');
    }
  };

  const handleCancelPurchase = async () => {
    if (!userId) return;
    await supabase
      .from('workday_client_profiles')
      .update({ pending_checkout: false, plan: 'free' })
      .eq('id', userId);
    navigate('/workday-client/dashboard');
  };

  const features = [
    { icon: Users,     label: '3 Team Seats',        sub: '+$99.99 each extra' },
    { icon: Briefcase, label: '3 Free Job Postings',  sub: 'per billing cycle' },
    { icon: Shield,    label: 'Full Pro Access',       sub: 'all features unlocked' },
    { icon: Calendar,  label: 'Grace on Cancel',       sub: 'access until period ends' },
  ];

  // ── Awaiting webhook confirmation — show full-screen spinner with context ───
  if (checkoutState === 'awaiting_approval' || checkoutState === 'approved') {
    return (
      <div className="min-h-screen bg-white flex flex-col text-gray-900">
        <LogoHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-xs px-4">
            <Loader2 className="w-10 h-10 text-[#40E0D0] animate-spin mx-auto mb-4" />
            <p className="text-gray-800 font-semibold mb-1">
              {checkoutState === 'approved' ? 'Redirecting…' : 'Confirming your subscription…'}
            </p>
            <p className="text-gray-400 text-sm">
              {checkoutState === 'approved'
                ? 'Pro access confirmed. Taking you to your dashboard.'
                : 'PayPal confirmed your payment. Waiting for account activation — this takes 5–20 seconds.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col text-gray-900">

      <LogoHeader />

      <div className="flex-1 flex flex-col items-center justify-center px-4 py-16 bg-white">

        {/* Page heading */}
        <div className="text-center mb-10 max-w-lg">
          <div className="inline-flex items-center gap-2 bg-[#40E0D0]/10 border border-[#40E0D0]/30 text-[#40E0D0] text-xs font-bold px-4 py-1.5 rounded-full tracking-widest uppercase mb-4">
            <Star className="w-3.5 h-3.5" />
            One Step Away
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">
            Activate Your Pro Plan
          </h1>
          <p className="text-gray-500 text-sm">
            You're one step away from activating Pro. If you leave this page, your account will remain on the Free plan until you upgrade.
          </p>
        </div>

        <div className="w-full max-w-xl">

          {/* Billing cycle toggle */}
          <div className="bg-gray-100 border border-gray-200 rounded-2xl p-1 flex mb-6">
            <button
              onClick={() => handleCycleChange('monthly')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                cycle === 'monthly'
                  ? 'bg-[#40E0D0] text-white shadow-md shadow-[#40E0D0]/25'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              Monthly — $499/mo
            </button>
            <button
              onClick={() => handleCycleChange('yearly')}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                cycle === 'yearly'
                  ? 'bg-[#40E0D0] text-white shadow-md shadow-[#40E0D0]/25'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              Yearly — $5,988/yr
            </button>
          </div>

          {/* Plan card */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 mb-6 shadow-sm">
            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-[#40E0D0] mb-1">Pro Plan</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-extrabold text-gray-900">
                    {cycle === 'yearly' ? '$5,988' : '$499'}
                  </span>
                  <span className="text-gray-400 text-sm">
                    /{cycle === 'yearly' ? 'year' : 'mo'}
                  </span>
                </div>
                <p className="text-gray-400 text-xs mt-1">
                  {cycle === 'yearly' ? 'Billed annually, cancel anytime' : 'Billed monthly, cancel anytime'}
                </p>
              </div>
              <div className="p-2.5 rounded-xl bg-[#40E0D0]/10 border border-[#40E0D0]/20">
                <Star className="w-5 h-5 text-[#40E0D0]" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-5">
              {features.map(({ icon: Icon, label, sub }) => (
                <div key={label} className="bg-gray-50 border border-gray-100 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-3.5 h-3.5 text-[#40E0D0]" />
                    <span className="text-xs font-semibold text-gray-800">{label}</span>
                  </div>
                  <p className="text-xs text-gray-400 pl-5">{sub}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 pt-4 mb-5">
              {[
                'Browse the full Workday talent pool',
                'Self-service job postings',
                'Candidate pipeline & direct messaging',
                'Pro job posting (company revealed)',
                'Document management',
                'Priority support',
              ].map((f) => (
                <div key={f} className="flex items-center gap-2 py-1">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  <span className="text-xs text-gray-600">{f}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 pt-4 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Total today</span>
              <span className="text-lg font-extrabold text-[#40E0D0]">
                {cycle === 'yearly' ? '$5,988.00' : '$499.00'}
              </span>
            </div>
          </div>

          {/* PayPal / error area */}
          <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
            <p className="text-xs text-gray-400 text-center mb-4">
              Secure checkout powered by PayPal. Cancel anytime from your billing settings.
            </p>

            {sdkError && (
              <div className="flex flex-col items-center gap-3 py-6">
                <div className="flex items-center gap-2 text-red-500 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {sdkError}
                </div>
                <button
                  onClick={() => window.location.reload()}
                  className="text-xs text-[#40E0D0] hover:underline"
                >
                  Reload and try again
                </button>
              </div>
            )}

            {checkoutState === 'failed' && !sdkError && (
              <div className="flex flex-col items-center gap-3 py-4">
                <div className="flex items-center gap-2 text-red-500 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {checkoutError}
                </div>
                <button
                  onClick={handleRetry}
                  className="flex items-center gap-1.5 text-xs text-[#40E0D0] hover:underline"
                >
                  <RefreshCw className="w-3 h-3" />
                  Try again
                </button>
              </div>
            )}

            {!sdkLoaded && !sdkError && (
              <div className="flex items-center justify-center py-8 gap-3 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading secure checkout…</span>
              </div>
            )}

            {checkoutState === 'creating' && (
              <div className="flex items-center justify-center py-4 gap-2 text-gray-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Opening PayPal checkout…
              </div>
            )}

            <div
              id="paypal-button-container"
              className={(!sdkLoaded || sdkError || checkoutState !== 'idle') ? 'hidden' : ''}
            />
          </div>

          <button
            onClick={handleCancelPurchase}
            className="w-full mt-6 text-sm text-gray-500 hover:text-gray-800 underline"
          >
            Cancel
          </button>

        </div>
      </div>
    </div>
  );
}
